import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { loginAdmin } from "../../services/auth";
import LoadingState from "../Common/LoadingState";
import AuthNavbar from "./AuthNavbar";
import { useTurnstile } from "../../hooks/useTurnstile";
import "./Auth.css";

// Back button component
const BackButton: React.FC = () => {
  return (
    <Link
      to="https://ukti.ai"
      className="flex items-center text-sm text-[color:var(--muted-foreground)] hover:text-[color:var(--color-secondary)] transition-colors mb-4"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="mr-1"
      >
        <path d="M19 12H5M12 19l-7-7 7-7" />
      </svg>
      Back to Home
    </Link>
  );
};

interface AdminLoginProps {
  onLoginSuccess: () => void;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Turnstile integration
  const turnstileerror = useCallback(() => {
    setError("Security verification failed. Please try again.");
  }, []);
  const turnstileexpire = useCallback(() =>  {
    setError("Security verification expired. Please try again.");
  }, []);
  const { turnstileContainer, getToken, hasToken, resetTurnstile, isDevelopment } = useTurnstile({
    onError: turnstileerror,
    onExpire: turnstileexpire,
  });

  

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim() || !password.trim()) {
      setError("Please enter both username and password");
      return;
    }

    // Check Turnstile verification
    if (!isDevelopment && !hasToken()) {
      setError("Please complete the security check");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const turnstileToken = getToken();
      const result = await loginAdmin(username, password, turnstileToken || undefined);

      if (result.success) {
        onLoginSuccess();
      } else {
        setError(result.error || "Login failed");
        // Reset the Turnstile widget on error
        resetTurnstile();
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      console.error(err);
      // Reset the Turnstile widget on error
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AuthNavbar />
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)] py-12 px-4 sm:px-6 lg:px-8 relative pt-20">
        <motion.div
          className="max-w-md w-full bg-[color:var(--card)] rounded-lg shadow-xl p-8"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="flex flex-col items-center mb-8">
            <motion.h2
              className="text-3xl pt-2 font-bold text-[color:var(--foreground)]"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Voice Agent Admin Portal
            </motion.h2>
            <motion.p
              className="text-sm text-[color:var(--muted-foreground)] mt-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Sign in to your admin account
            </motion.p>
          </div>

          <motion.form
            onSubmit={handleSubmit}
            className="space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <label
                htmlFor="username"
                className="block text-sm font-medium text-[color:var(--foreground)]"
              >
                Username
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                autoComplete="username"
                disabled={loading}
                className="mt-1 block w-full px-3 py-2 bg-transparent border border-[color:var(--border)] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-secondary)] focus:border-transparent text-[color:var(--foreground)] [&:-webkit-autofill]:text-[color:var(--foreground)] [&:-webkit-autofill]:bg-transparent [&:-webkit-autofill]:!transition-colors"
              />
            </motion.div>

            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <label
                htmlFor="password"
                className="block text-sm font-medium text-[color:var(--foreground)]"
              >
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={loading}
                className="mt-1 block w-full px-3 py-2 bg-transparent border border-[color:var(--border)] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-secondary)] focus:border-transparent text-[color:var(--foreground)] [&:-webkit-autofill]:text-[color:var(--foreground)] [&:-webkit-autofill]:bg-transparent [&:-webkit-autofill]:!transition-colors"
              />
            </motion.div>

            {/* Turnstile Widget - Only show in production */}
            {!isDevelopment ? (
              <motion.div
                className="flex justify-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                <div ref={turnstileContainer}></div>
              </motion.div>
            ) : (
              <motion.div
                className="p-3 bg-blue-50 border border-blue-200 text-blue-600 rounded-md text-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                Development mode: Turnstile verification bypassed
              </motion.div>
            )}

            {error && (
              <motion.div
                className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                {error}
              </motion.div>
            )}

            <motion.button
              type="submit"
              disabled={loading}
              className="w-full py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              {loading ? "Logging in..." : "Login"}
            </motion.button>
          </motion.form>

          <motion.div
            className="mt-6 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.9 }}
          >
            <Link
              to="/login"
              className="text-sm text-[color:var(--foreground)] hover:text-[color:var(--color-secondary)] font-medium"
            >
              Sign in with OTP
            </Link>
          </motion.div>

          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-10">
              <LoadingState
                size="medium"
                padding="none"
                message="Logging in..."
                fullPage={false}
              />
            </div>
          )}
        </motion.div>
      </div>
    </>
  );
};

export default AdminLogin;
